import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHART_KIND_DEFS,
  DATE_GRAIN_LABELS,
  DATE_GRAINS,
  REPORT_MODULE_DEFS,
  REPORT_MODULE_MAP,
  type ChartKind,
  type DateGrain,
  type ReportDefinition,
  type ReportModule,
  type ReportPreset,
  type ReportResult,
  type SourceDef,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { plural, WORDS } from '../../lib/format';
import { exportReport, printReportPath } from '../../lib/reportExport';
import { Chart, formatValue } from '../../components/Chart';
import { DocPreview } from '../../components/DocPreview';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead, Stat } from '../../components/ui';
import { useToast } from '../../components/Toast';

interface Catalog {
  sources: SourceDef[];
  presets: ReportPreset[];
}

interface SavedReport {
  id: string;
  name: string;
  description: string | null;
  isShared: boolean;
  isMine: boolean;
  module: ReportModule | null;
  showOnHome: boolean;
  showOnModule: boolean;
  definition: ReportDefinition;
}

const BLANK: ReportDefinition = {
  source: 'INVOICES',
  dimension: 'issuedAt',
  measure: 'total',
  chart: 'LINE',
  grain: 'MONTH',
  filters: {},
  limit: 20,
  sort: 'VALUE_DESC',
};

/**
 * باني التقارير.
 *
 * السؤال يُبنى من أربعة اختيارات: من أي بيانات، ما الذي يُقاس، بأي تقسيم،
 * وبأي شكل. ما يمكن اختياره يأتي من الخادم لا من الواجهة، فلا تعرض الشاشة
 * تركيبة يرفضها الخادم.
 */
export function ReportBuilder() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [def, setDef] = useState<ReportDefinition>(BLANK);
  const [saving, setSaving] = useState(false);
  const [openSaved, setOpenSaved] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [loadedFrom, setLoadedFrom] = useState<SavedReport | null>(null);

  /*
   * ما يصل في الرابط: `?id=` تقريرٌ محفوظ (تعديلٌ من لوحة)، و`?def=` تعريفٌ
   * عابر (بطاقةٌ جاهزة تُفتح لتعديلها)، و`?module=` أو `?home` تقولان أين
   * يُنشر ما سيُحفظ. كان `?def=` يُرسَل ولا يُقرأ فيفتح الباني فارغًا.
   */
  const wantedModule = (params.get('module') as ReportModule | null) ?? null;
  const wantedHome = params.get('home') === '1';
  const wantedId = params.get('id');
  const wantedDef = params.get('def');

  useEffect(() => {
    if (wantedDef) {
      try {
        const parsed = JSON.parse(wantedDef) as Partial<ReportDefinition>;
        setDef({ ...BLANK, ...parsed, filters: parsed.filters ?? {} });
        setLoadedFrom(null);
      } catch {
        toast('تعريفُ التقرير في الرابط غير صالح');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedDef]);

  const wanted = useQuery({
    queryKey: ['reports', 'saved', wantedId],
    queryFn: () => api<SavedReport>(`/reports/saved/${wantedId}`),
    enabled: !!wantedId,
  });
  useEffect(() => {
    if (!wanted.data) return;
    setDef({ ...BLANK, ...wanted.data.definition, filters: wanted.data.definition.filters ?? {} });
    setLoadedFrom(wanted.data);
    /* الرابطُ استُهلك: بقاؤه يعيد التحميلَ فوق تعديلات المستخدم عند كلّ تحديث. */
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted.data]);

  const catalog = useQuery({
    queryKey: ['reports', 'catalog'],
    queryFn: () => api<Catalog>('/reports/catalog'),
    staleTime: Infinity,
  });

  const saved = useQuery({
    queryKey: ['reports', 'saved'],
    queryFn: () => api<{ items: SavedReport[] }>('/reports/saved'),
  });

  const source = useMemo(
    () => catalog.data?.sources.find((s) => s.key === def.source),
    [catalog.data, def.source],
  );
  const dimension = source?.dimensions.find((d) => d.key === def.dimension);
  const measure = source?.measures.find((m) => m.key === def.measure);
  const isDate = dimension?.kind === 'DATE';

  /*
   * تغيير المصدر يُبطل التقسيم والقياس المختارين.
   *
   * "المرحلة" تخص الصفقات ولا معنى لها في المصروفات. بقاؤها بعد التبديل
   * يرسل للخادم تركيبة يرفضها، فيرى المستخدم خطأً بدل تقرير.
   */
  const pickSource = (key: string) => {
    const next = catalog.data?.sources.find((s) => s.key === key);
    if (!next) return;
    const firstDimension = next.dimensions[0]!;
    setDef({
      ...BLANK,
      source: key,
      dimension: firstDimension.key,
      measure: next.measures[0]!.key,
      chart: firstDimension.kind === 'DATE' ? 'LINE' : 'BAR',
      grain: 'MONTH',
      from: def.from,
      to: def.to,
    });
    setLoadedFrom(null);
  };

  const patch = (changes: Partial<ReportDefinition>) => {
    setDef((d) => {
      const next = { ...d, ...changes };
      // الخط الزمني بلا محور زمني لا يُرسم، فيتحوّل إلى أعمدة تلقائيًا.
      const dim = source?.dimensions.find((x) => x.key === next.dimension);
      if (next.chart === 'LINE' && dim?.kind !== 'DATE') next.chart = 'BAR';
      return next;
    });
  };

  const result = useQuery({
    queryKey: ['reports', 'run', def],
    queryFn: () => api<ReportResult>('/reports/run', { method: 'POST', body: def }),
    enabled: Boolean(source && dimension && measure),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/reports/saved/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'saved'] });
      setLoadedFrom(null);
    },
  });

  return (
    <div className="page">
      <PageHead
        title="باني التقارير"
        subtitle={loadedFrom ? `محمَّل من: ${loadedFrom.name}` : 'ابنِ سؤالك ثم احفظه'}
        actions={
          <>
            <button className="btn" onClick={() => setOpenSaved(true)}>
              <Icon name="book" size={15} />
              التقارير المحفوظة
              {saved.data && saved.data.items.length > 0 && (
                <span className="tiny mute"> ({saved.data.items.length})</span>
              )}
            </button>
            <button
              className="btn"
              onClick={() => void exportReport(def, loadedFrom?.name ?? 'تقرير', 'xlsx')}
              disabled={!result.data}
              title="تصدير إكسل"
            >
              <Icon name="download" size={15} />
              إكسل
            </button>
            <button
              className="btn"
              onClick={() => setPrinting(true)}
              disabled={!result.data}
              title="طباعة أو حفظ PDF"
            >
              <Icon name="clipboard" size={15} />
              PDF
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setSaving(true)}
              disabled={!result.data}
            >
              <Icon name="check" size={15} />
              {loadedFrom ? 'حفظ ونشر' : 'حفظ التقرير'}
            </button>
          </>
        }
      />

      {printing && (
        <DocPreview
          path={printReportPath(def, loadedFrom?.name ?? 'تقرير')}
          title={loadedFrom?.name ?? 'تقرير'}
          onClose={() => setPrinting(false)}
        />
      )}

      {catalog.isPending && <div className="skeleton" style={{ height: 300 }} />}
      {catalog.isError && (
        <ErrorState error={catalog.error} onRetry={() => void catalog.refetch()} />
      )}

      {catalog.data && (
        <>
          {/* ---- التقارير الجاهزة ---- */}
          <div className="preset-strip">
            <span className="tiny mute" style={{ alignSelf: 'center' }}>
              ابدأ من:
            </span>
            {catalog.data.presets.map((p) => (
              <button
                key={p.key}
                className="btn btn-sm"
                title={p.description}
                onClick={() => {
                  setDef({ ...BLANK, ...p.definition, filters: p.definition.filters ?? {} });
                  setLoadedFrom(null);
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="builder">
            {/* ================= لوحة البناء ================= */}
            <div className="card builder-panel">
              <div className="card-head">
                <h2 className="card-title">تعريف التقرير</h2>
              </div>

              <div className="card-body">
                <div className="field">
                  <label className="label" htmlFor="r-source">
                    مصدر البيانات
                  </label>
                  <select
                    id="r-source"
                    className="select"
                    value={def.source}
                    onChange={(e) => pickSource(e.target.value)}
                  >
                    {catalog.data.sources.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  {source && <span className="tiny mute">{source.description}</span>}
                </div>

                <div className="field">
                  <label className="label" htmlFor="r-measure">
                    ما الذي يُقاس
                  </label>
                  <select
                    id="r-measure"
                    className="select"
                    value={def.measure}
                    onChange={(e) => patch({ measure: e.target.value })}
                  >
                    {source?.measures.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label className="label" htmlFor="r-dimension">
                    التقسيم
                  </label>
                  <select
                    id="r-dimension"
                    className="select"
                    value={def.dimension}
                    onChange={(e) => patch({ dimension: e.target.value })}
                  >
                    {source?.dimensions.map((d) => (
                      <option key={d.key} value={d.key}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isDate && (
                  <div className="field">
                    <label className="label" htmlFor="r-grain">
                      الدرجة الزمنية
                    </label>
                    <select
                      id="r-grain"
                      className="select"
                      value={def.grain ?? 'MONTH'}
                      onChange={(e) => patch({ grain: e.target.value as DateGrain })}
                    >
                      {DATE_GRAINS.map((g) => (
                        <option key={g} value={g}>
                          {DATE_GRAIN_LABELS[g]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <h4 className="section-title">شكل العرض</h4>
                <div className="chart-picker">
                  {CHART_KIND_DEFS.map((c) => {
                    const disabled = c.key === 'LINE' && !isDate;
                    return (
                      <button
                        key={c.key}
                        className={`chart-option${def.chart === c.key ? ' is-active' : ''}`}
                        disabled={disabled}
                        title={disabled ? 'يحتاج تقسيمًا بالتاريخ' : c.hint}
                        onClick={() => patch({ chart: c.key as ChartKind })}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                <span className="tiny mute">
                  {CHART_KIND_DEFS.find((c) => c.key === def.chart)?.hint}
                </span>

                <h4 className="section-title">النطاق والتصفية</h4>

                <div className="form-grid">
                  <div className="field">
                    <label className="label" htmlFor="r-from">
                      من تاريخ
                    </label>
                    <input
                      id="r-from"
                      className="input"
                      type="date"
                      value={def.from ?? ''}
                      onChange={(e) => patch({ from: e.target.value || null })}
                    />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="r-to">
                      إلى تاريخ
                    </label>
                    <input
                      id="r-to"
                      className="input"
                      type="date"
                      value={def.to ?? ''}
                      onChange={(e) => patch({ to: e.target.value || null })}
                    />
                  </div>
                </div>

                {source?.filters.map((f) => (
                  <div className="field" key={f.key}>
                    <label className="label" htmlFor={`r-f-${f.key}`}>
                      {f.label}
                    </label>
                    {f.kind === 'ENUM' && f.labels ? (
                      <select
                        id={`r-f-${f.key}`}
                        className="select"
                        value={def.filters?.[f.key] ?? ''}
                        onChange={(e) =>
                          patch({ filters: { ...def.filters, [f.key]: e.target.value } })
                        }
                      >
                        <option value="">الكل</option>
                        {Object.entries(f.labels).map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <RefFilter
                        id={`r-f-${f.key}`}
                        filter={f}
                        value={def.filters?.[f.key] ?? ''}
                        onChange={(v) => patch({ filters: { ...def.filters, [f.key]: v } })}
                      />
                    )}
                  </div>
                ))}

                {/* التقسيم الزمني يُرتَّب بالتاريخ دائمًا ولا يُقص، فلا معنى لعرض الخيارين. */}
                {!isDate && (
                  <div className="form-grid mt-4">
                    <div className="field">
                      <label className="label" htmlFor="r-sort">
                        الترتيب
                      </label>
                      <select
                        id="r-sort"
                        className="select"
                        value={def.sort ?? 'VALUE_DESC'}
                        onChange={(e) =>
                          patch({ sort: e.target.value as ReportDefinition['sort'] })
                        }
                      >
                        <option value="VALUE_DESC">الأعلى قيمة أولًا</option>
                        <option value="VALUE_ASC">الأقل قيمة أولًا</option>
                        <option value="LABEL_ASC">بالاسم</option>
                      </select>
                    </div>

                    <div className="field">
                      <label className="label" htmlFor="r-limit">
                        عدد الصفوف
                      </label>
                      <input
                        id="r-limit"
                        className="input num"
                        type="number"
                        min="1"
                        max="200"
                        value={def.limit ?? 20}
                        onChange={(e) => patch({ limit: Number(e.target.value) || 20 })}
                      />
                      <span className="tiny mute">الباقي يُجمَّع في صف "أخرى".</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ================= النتيجة ================= */}
            <div className="builder-result">
              {result.isError && (
                <ErrorState error={result.error} onRetry={() => void result.refetch()} />
              )}

              {result.isPending && <div className="skeleton" style={{ height: 360 }} />}

              {result.data && (
                <>
                  <div className="stats">
                    <Stat
                      label={result.data.measureLabel}
                      value={
                        <span className="num">
                          {formatValue(result.data.total, result.data.format)}
                        </span>
                      }
                      note={`${result.data.sourceLabel} — ${result.data.dimensionLabel}`}
                    />
                    <Stat label="السجلات" value={result.data.records} note="الداخلة في التقرير" />
                    <Stat
                      label="الفئات"
                      value={result.data.rows.length}
                      note={plural(result.data.rows.length, WORDS.row)}
                    />
                  </div>

                  {result.data.rows.length === 0 ? (
                    <EmptyState
                      title="لا بيانات"
                      text="لا سجلات تطابق النطاق والتصفية المحددين. وسّع النطاق الزمني أو أزل مرشّحًا."
                    />
                  ) : (
                    <div className="card">
                      <div className="card-head">
                        <h2 className="card-title">
                          {result.data.measureLabel} حسب {result.data.dimensionLabel}
                        </h2>
                      </div>
                      <div className="card-body">
                        {def.chart !== 'TABLE' && (
                          <Chart
                            kind={def.chart}
                            rows={result.data.rows}
                            format={result.data.format}
                          />
                        )}

                        {(def.chart === 'TABLE' || def.chart === 'PIE') && (
                          <div className="table-wrap mt-4">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>{result.data.dimensionLabel}</th>
                                  <th style={{ width: 160 }}>{result.data.measureLabel}</th>
                                  <th style={{ width: 100 }}>النسبة</th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.data.rows.map((r) => (
                                  <tr key={r.key}>
                                    <td className="cell-strong">{r.label}</td>
                                    <td className="num">
                                      {formatValue(r.value, result.data!.format)}
                                    </td>
                                    <td className="num cell-mute">{r.share}٪</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td className="cell-strong">الإجمالي</td>
                                  <td className="num cell-strong">
                                    {formatValue(result.data.total, result.data.format)}
                                  </td>
                                  <td className="num cell-mute">100٪</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {saving && (
        <SaveModal
          definition={def}
          existing={loadedFrom}
          defaultModule={wantedModule}
          defaultHome={wantedHome}
          onClose={() => setSaving(false)}
          onSaved={(report) => {
            setSaving(false);
            setLoadedFrom(report);
            void queryClient.invalidateQueries({ queryKey: ['reports', 'saved'] });
          }}
        />
      )}

      {openSaved && (
        <Modal
          title="التقارير المحفوظة"
          onClose={() => setOpenSaved(false)}
          wide
          footer={
            <button className="btn btn-primary" onClick={() => setOpenSaved(false)}>
              إغلاق
            </button>
          }
        >
          {saved.data?.items.length === 0 && (
            <EmptyState
              title="لا تقارير محفوظة"
              text="ابنِ تقريرًا ثم احفظه ليعود إليه فريقك بضغطة."
            />
          )}

          <div className="col" style={{ gap: 8 }}>
            {saved.data?.items.map((r) => (
              <div key={r.id} className="rule-row">
                <div className="col" style={{ gap: 2, flex: 1 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="strong small">{r.name}</span>
                    <Badge kind={r.isShared ? 'info' : 'neutral'}>
                      {r.isShared ? 'مشترك' : 'خاص'}
                    </Badge>
                    {r.showOnHome && <Badge kind="brand">الرئيسية</Badge>}
                    {r.showOnModule && r.module && (
                      <Badge kind="success">لوحة {REPORT_MODULE_MAP[r.module].label}</Badge>
                    )}
                  </div>
                  {r.description && <p className="tiny mute">{r.description}</p>}
                </div>

                <div className="row" style={{ gap: 6 }}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setDef({ ...BLANK, ...r.definition, filters: r.definition.filters ?? {} });
                      setLoadedFrom(r);
                      setOpenSaved(false);
                    }}
                  >
                    فتح
                  </button>
                  {r.isMine && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => remove.mutate(r.id)}
                      disabled={remove.isPending}
                    >
                      حذف
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ========================================================================== */

/** مرشّح على مرجع: يحمّل خياراته من مصدرها. */
function RefFilter({
  id,
  filter,
  value,
  onChange,
}: {
  id: string;
  filter: { key: string; refTable?: string };
  value: string;
  onChange: (v: string) => void;
}) {
  const path =
    filter.refTable === 'users'
      ? '/auth/members'
      : filter.refTable === 'suppliers'
        ? '/finance/suppliers?pageSize=100'
        : '/crm/accounts?pageSize=100&sort=name&order=asc';

  const options = useQuery({
    queryKey: ['ref-options', path],
    queryFn: () => api<{ items: Array<{ id?: string; userId?: string; name: string }> }>(path),
    staleTime: 5 * 60_000,
  });

  return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">الكل</option>
      {options.data?.items.map((o) => {
        const key = o.userId ?? o.id!;
        return (
          <option key={key} value={key}>
            {o.name}
          </option>
        );
      })}
    </select>
  );
}

function SaveModal({
  definition,
  existing,
  defaultModule,
  defaultHome,
  onClose,
  onSaved,
}: {
  definition: ReportDefinition;
  existing: SavedReport | null;
  defaultModule: ReportModule | null;
  defaultHome: boolean;
  onClose: () => void;
  onSaved: (report: SavedReport) => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [isShared, setIsShared] = useState(existing?.isShared ?? false);
  const [module, setModule] = useState<ReportModule | ''>(existing?.module ?? defaultModule ?? '');
  const [showOnModule, setShowOnModule] = useState(existing?.showOnModule ?? !!defaultModule);
  const [showOnHome, setShowOnHome] = useState(existing?.showOnHome ?? defaultHome);
  const [asNew, setAsNew] = useState(!existing);

  useEffect(() => {
    if (!existing) setAsNew(true);
  }, [existing]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || undefined,
        isShared,
        module: module || null,
        showOnModule: !!module && showOnModule,
        showOnHome,
        definition,
      };
      return existing && !asNew
        ? api<SavedReport>(`/reports/saved/${existing.id}`, { method: 'PATCH', body })
        : api<SavedReport>('/reports/saved', { method: 'POST', body });
    },
    onSuccess: onSaved,
  });

  return (
    <Modal
      title="حفظ التقرير"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={name.trim().length < 2 || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <p className="small mute mb-4">
        يُحفظ السؤال لا الجواب: التقرير يُعاد حسابه من البيانات في كل مرة يُفتح.
      </p>

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="s-name">
            اسم التقرير
          </label>
          <input
            id="s-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="s-desc">
            الوصف
          </label>
          <textarea
            id="s-desc"
            className="textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      {/*
        النشر: التقريرُ بطاقةً في لوحة.
        الوحدةُ تقول أيَّ لوحةٍ يخصّ، و«الرئيسية» تضعه في لوحة النظام. والمنشورُ
        يراه الفريقُ كلُّه ضمنًا — لوحةٌ مشتركةٌ لا تحمل تقريرًا خاصًّا.
      */}
      <h4 className="section-title mt-4">النشر على اللوحات</h4>
      <div className="form-grid">
        <div className="field span-2">
          <label className="label" htmlFor="s-module">
            الوحدة
          </label>
          <select
            id="s-module"
            className="select"
            value={module}
            onChange={(e) => setModule(e.target.value as ReportModule | '')}
          >
            <option value="">— بلا وحدة —</option>
            {REPORT_MODULE_DEFS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="col mt-3">
        <label className="row" style={{ cursor: module ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={!!module && showOnModule}
            disabled={!module}
            onChange={(e) => setShowOnModule(e.target.checked)}
          />
          <span className="small">
            يظهر في لوحة {module ? REPORT_MODULE_MAP[module].label : 'الوحدة'}
          </span>
        </label>
        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showOnHome}
            onChange={(e) => setShowOnHome(e.target.checked)}
          />
          <span className="small">يظهر في لوحة النظام بالرئيسية</span>
        </label>
      </div>

      <div className="col mt-4">
        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isShared || showOnHome || (!!module && showOnModule)}
            disabled={showOnHome || (!!module && showOnModule)}
            onChange={(e) => setIsShared(e.target.checked)}
          />
          <span className="small">مشترك مع الفريق</span>
        </label>

        {existing && (
          <label className="row" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={asNew} onChange={(e) => setAsNew(e.target.checked)} />
            <span className="small">حفظ كتقرير جديد بدل تعديل "{existing.name}"</span>
          </label>
        )}
      </div>
    </Modal>
  );
}
